/*
 * A pill-styled customer select that searches customers via the API.
 * Used in the Quote editor where controls render as pills.
 */
import { useCallback, useEffect } from 'react'
import {
  Text,
  ControlContextProvider,
  CustomerLogo,
  GreyGrey20,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Pill,
  PopoverWrapper,
  Primary13Regular,
  RedRed50,
  SelectControl,
  useAsyncOptions,
  useControlContext,
  useModalState,
  Center,
  Flex
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { SECONDS } from '@sequencehq/utils/dates'
import { CreateCustomerForm } from 'modules/Customers/components/drawers/CreateCustomer/CreateCustomerForm'
import { CustomerPreviewCard } from 'Customer/components/CustomerPreviewCard'
import { getLogoSrc } from 'Customer/utils/logo'
import { useCustomerPaginatedFetcher } from 'Customer/hooks/useCustomerPaginatedFetcher'
import { FC } from 'react'

export interface CustomerSelectPillProps {
  value?: string
  onChange: (customerId: string) => void
  disabled?: boolean
  allowCreate?: boolean
  isInvalid?: boolean
  'data-testid'?: string
  variant?: 'default' | 'ghost'
  noBorder?: boolean
}

export const CustomerSelectPill: FC<CustomerSelectPillProps> = props => {
  return (
    <ControlContextProvider showSearchInPopover matchWidth={false}>
      <CustomerSelectPillCore {...props} />
    </ControlContextProvider>
  )
}

const CustomerSelectPillCore: FC<CustomerSelectPillProps> = ({
  value = '',
  onChange,
  disabled = false,
  variant = 'default',
  allowCreate = true,
  isInvalid = false,
  'data-testid': dataTestId,
  noBorder = false
}) => {
  const { searchTerm, setSearchTerm, isOpen } = useControlContext()
  const { openModal, isModalOpen, closeModal } = useModalState()

  const { data: selectedCustomer } = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: value || '' },
    { enabled: !!value, staleTime: 20 * SECONDS }
  )

  const { paginatedFetcher, mapToOption } = useCustomerPaginatedFetcher()

  const {
    options,
    isLoading,
    search,
    clearSearch,
    fetchMore,
    hasMore,
    isFetchingMore
  } = useAsyncOptions({
    paginatedFetcher,
    mapToOption,
    skipInitialFetch: true
  })

  useEffect(() => {
    search(searchTerm)
  }, [searchTerm, search])

  const handleChange = (val: string) => {
    onChange(val)
  }

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSearchTerm('')
        clearSearch()
        search('')
      }
    },
    [setSearchTerm, clearSearch, search]
  )

  const createDependentProps = allowCreate
    ? {
        createLabel: 'Add a customer',
        onCreate: openModal
      }
    : {}

  const pillLabel = isInvalid
    ? 'Customer'
    : (selectedCustomer?.legalName ?? 'Customer')

  const hasSelection = !!value && !!selectedCustomer

  // Show the customer's logo in the pill (matching CustomerLogoById elsewhere).
  // CustomerLogo falls back to IconBuildings when there's no logo.
  const logoSrc =
    hasSelection && !isInvalid
      ? getLogoSrc({ domain: selectedCustomer?.domain })
      : undefined

  const pill = noBorder ? (
    <Flex
      paddingX="6px"
      minHeight="24px"
      gap="6px"
      borderRadius="6px"
      _hover={!disabled ? { background: GreyGrey20 } : undefined}
      background={isOpen ? GreyGrey20 : undefined}
      justifyContent="center"
      alignItems="center"
      maxWidth="190px"
      data-testid={dataTestId}
      data-disabled={disabled}
      cursor={disabled ? 'not-allowed' : 'pointer'}
    >
      <Center flexShrink="0" color={GreyGrey70}>
        <CustomerLogo src={logoSrc} size={16} />
      </Center>
      <Text
        {...Primary13Regular}
        color={isInvalid ? RedRed50 : hasSelection ? GreyGrey80 : GreyGrey60}
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {pillLabel}
      </Text>
    </Flex>
  ) : (
    <Pill
      interactive
      disabled={disabled}
      icon={<CustomerLogo src={logoSrc} size={16} />}
      error={isInvalid}
      data-testid={dataTestId}
      variant={variant}
    >
      {pillLabel}
    </Pill>
  )

  const showPreview = !!value && !isOpen

  const trigger = showPreview ? (
    <PopoverWrapper
      popover={<CustomerPreviewCard customerId={value} />}
      disabled={isOpen}
      openDelay={500}
    >
      {pill}
    </PopoverWrapper>
  ) : (
    pill
  )

  return (
    <>
      <SelectControl
        initialValue={value}
        options={options}
        onChange={handleChange}
        placeholder="Customers"
        {...createDependentProps}
        disabled={disabled}
        trigger={trigger}
        filterOptionGroups={groups => groups}
        data-testid={dataTestId}
        onToggle={handleToggle}
        onScrollEnd={hasMore ? fetchMore : undefined}
        isLoadingMore={isLoading || isFetchingMore}
      />

      {isModalOpen && (
        <CreateCustomerForm
          onClose={closeModal}
          onSuccess={({ id }: { id: string }) => {
            closeModal()
            onChange(id)
          }}
        />
      )}
    </>
  )
}
